import {
  directionsState,
  mainState,
  tuckState
} from '@/stores'
import {
  CANNON,
  THREE,
  MobileDetector,
  playersManager,
  ControlsTypes,
  modes,
  settings,
  SettingsTypes
} from '@powerplay/core-minigames'
import {
  gameConfig,
  unitCyclesConfig
} from '../config'
import type { VectorXZ } from '../types'
import { PlayerInputsUnitCycleManager } from './PlayerInputsUnitCycleManager'
import { player } from '.'
import { inputsManager } from '../InputsManager'

/**
 * Trieda pre spravu rychlosti hraca
 */
export class PlayerVelocityManager {

  /** Nova rychlost */
  public velocity = new CANNON.Vec3()

  /** Objekt jednotkovej kruznice pre playerove inputy */
  private playerInputsUnitCycle = new PlayerInputsUnitCycleManager()

  /** Posledna hracova rotacia na osi y */
  private lastPlayerRotationY = 0

  /** Pomocny objekt na vypocty */
  private tempObject = new THREE.Object3D()

  /** Pomocny vektor */
  private tempVector = new THREE.Vector3()

  /** Aktualna max rychlost lyziara */
  private maxSpeed = 0

  /** zmena rychlosti na XZ rovine */
  private prevVelocityHypotenuseXZ = 0

  /** velocity minuleho framu */
  private prevVelocity = new CANNON.Vec3()

  /**
   * Ziskanie rychlosti
   * @returns Rychlost
   */
  public getVelocity(): CANNON.Vec3 {

    return this.velocity

  }

  /**
   * Aktualizovanie veci podla konfigu od GD
   */
  public setAttributesGD(): void {

    this.maxSpeed = gameConfig.maxSpeedNormal

  }

  /**
   * Ziskanie rotacie na osi y z rychlosti
   * @param velocity - Vektor rychlosti
   * @returns Y rotacia
   */
  public getRotationYFromVelocity(velocity: CANNON.Vec3): number {

    this.tempVector.set(
      velocity.x,
      0,
      velocity.z
    )

    this.tempObject.position.set(0, 0, 0)
    this.tempObject.lookAt(this.tempVector)

    /**
     * DOLEZITE - lookAt dava iba rotaciu do Math.PI
     *
     * Ked davame nastavovanie rotacie podla lookAt, tak zaciname pohladom na zaporne Z, kde by
     * bola rotacia 0, potom po smere hodinovych ruciciek a po 45 stupnoch by to islo takto:
     * 0 .. PI/4 .. PI/2 .. PI/4 .. 0 .. -PI/4 .. -PI/2 .. -PI/4 .. 0
     * preto musime upravit danu rotaciu az pri vypocte, lebo keby sme ju upravili na objekte,
     * tak by sa zmenila znova na mensie
     */
    if (velocity.z > 0) {

      const sign = velocity.x >= 0 ? 1 : -1
      this.tempObject.rotation.y = (sign * Math.PI) - this.tempObject.rotation.y

    }

    return this.tempObject.rotation.y

  }

  /**
   * Vypocitanie interpolacie medzi dvomi rotaciami podla percenta
   * @param rotationYNew - Nova rotacia
   * @param rotationYLast - Posledna rotacia
   * @param percent - Percento (1 - pri new, 0 - pri last)
   * @returns Interpolovana rotacia
   */
  private getInterpolationOfNewAndLastRotationY(
    rotationYNew: number,
    rotationYLast: number,
    percent: number
  ): number {

    // ked je nova rotacia mensia, tak je iny vzorec ako opacne
    if (rotationYNew < rotationYLast) {

      return rotationYLast - (Math.abs(rotationYLast - rotationYNew) * percent)

    }

    return rotationYLast + (Math.abs(rotationYNew - rotationYLast) * percent)

  }

  /**
   * Vypocitanie sily, ktora bude pohanat lyziara dopredu
   * @param velocity - Rychlost
   * @returns Vektor na osiach X a Z
   */
  private getForceForward(velocity: CANNON.Vec3): VectorXZ {

    // urcime si zaklad podla atributu
    const playerStrength = playersManager.getPlayer().attribute.total
    let forwardForce = playerStrength / 1000 * 0.03
    if (playerStrength > 1000) forwardForce = 0.03 + ((playerStrength - 1000) / 1000 * 0.01)

    // ak je tuck, davame specialny prirastok este z konfigu
    if (player.isCrouching) forwardForce += unitCyclesConfig.forwardForce.coefTuck

    const velocitySum: number = Math.abs(velocity.x) + Math.abs(velocity.z)

    // vypocet je jednoduchy, pomerovo si urcime, kolko z koeficientu dame na danu os
    return {
      x: forwardForce * (velocity.x / velocitySum),
      z: forwardForce * (velocity.z / velocitySum)
    }

  }

  /**
   * Ziskanie aktualizovanej rotacie na zaklade hracovych inputov
   * @param velocity - Rychlost
   * @param percentInputs - percento hracovych inputov
   * @returns Rotacia
   */
  private getUpdatedRotationYByPlayerInputs(
    velocity: CANNON.Vec3,
    percentInputs: number
  ): number {

    // zistime spravnu rotaciu, ktora bude podla konfigu vela alebo malo ovplyvnovana fyzikou
    const interpolatedRotation: number = this.getInterpolationOfNewAndLastRotationY(
      this.getRotationYFromVelocity(velocity),
      this.lastPlayerRotationY,
      gameConfig.percentPhysicsRotationChange
    )

    // prirastok urcime podla toho, ako vela mame inputy nastavene
    const rotationAdd: number = percentInputs *
            unitCyclesConfig.playerInputs.coefDirectionChange

    return interpolatedRotation + rotationAdd

  }

  /**
   * Ziskanie hodnoty, ktorou sa nasobi rychlost, aby sme lyziara spomalovali
   * @param percentInputs - percento hracovych inputov
   * @param coef - koeficient
   * @returns Koeficient spomalovania
   */
  private getVelocityDecreaseValue(percentInputs: number, coef: number): number {

    return (1 - (Math.abs(percentInputs) * coef))

  }

  /**
   * Vypocitanie zmenenej hodnoty rychlosti po pridani force forward a spomaleni
   * @param velocity - rychlost
   * @param percentInputs - percento hracovych inputov
   * @returns Rychlost
   */
  private getChangedVelocity(velocity: CANNON.Vec3, percentInputs: number): CANNON.Vec3 {

    // forward force - pohyb dopredu
    const forceForward: VectorXZ = this.getForceForward(velocity)

    const coefTurnSlowing = 0.012 - playersManager.getPlayer().attribute.total / 2000 * 0.004

    // ubytok vo velocity podla inputov
    const velocityDecreaseValue: number = this.getVelocityDecreaseValue(
      percentInputs,
      coefTurnSlowing
    )

    return new CANNON.Vec3(
      (velocity.x + forceForward.x) * velocityDecreaseValue,
      velocity.y * velocityDecreaseValue,
      (velocity.z + forceForward.z) * velocityDecreaseValue
    )

  }

  /**
   * Ziskanie prepony rychlosti na osiach X a Z
   * @param velocityX - rychlost na X
   * @param velocityZ - rychlost na Z
   * @returns Prepona
   */
  private getVelocityHypotenuseXZ(velocityX: number, velocityZ: number): number {

    return Math.sqrt((velocityX ** 2) + (velocityZ ** 2))

  }

  /**
   * Redistribuovanie rychlosti na XZ
   * @param hypotenuseVelocityXZ - prepona rychlosti na XZ
   * @param rotationY - rotacia na osi Y
   * @returns Nova rychlost na XZ
   */
  private redistributeVelocityXZ(hypotenuseVelocityXZ: number, rotationY: number): VectorXZ {

    // pri rotaciach vacsich ako PI / 2 musime pocitat inak, lebo pocitame opacny trojuholnik
    if (rotationY > Math.PI / 2) {

      const newRotationChanged = rotationY - (Math.PI / 2)

      return {
        x: hypotenuseVelocityXZ * Math.cos(newRotationChanged),
        z: hypotenuseVelocityXZ * Math.sin(newRotationChanged) * -1
      }

    }

    return {
      x: hypotenuseVelocityXZ * Math.sin(rotationY),
      z: hypotenuseVelocityXZ * Math.cos(rotationY)
    }

  }

  /**
   * Nastavenie novej velocity podla roznych veci
   */
  public getNewVelocity(velocity: CANNON.Vec3, inAir = false, isStart = false): CANNON.Vec3 {

    // zatacanie vlavo / vpravo - nastavime parametre na jednotkovej kruznici pre hracov pohyb
    this.playerInputsUnitCycle.update(inAir)
    const playerInputsUnitCyclePercentage = this.playerInputsUnitCycle.calculatePercentage()

    const velocityChanged: CANNON.Vec3 = this.getChangedVelocity(
      velocity,
      playerInputsUnitCyclePercentage
    )

    // vypocitame si rychlost na 2 osiach, aby sme vedeli spravit distribuciu podla posunu
    const velocityHypotenuseXZ: number = this.getVelocityHypotenuseXZ(
      velocityChanged.x,
      velocityChanged.z
    )

    // vypocitame novu rotaciu na zaklade hracovych inputov
    const newRotation: number = this.getUpdatedRotationYByPlayerInputs(
      velocity,
      playerInputsUnitCyclePercentage
    )

    // redistribuujeme rychlost na XZ podla noveho uhla
    const newVelocityXZ: VectorXZ = this.redistributeVelocityXZ(
      velocityHypotenuseXZ,
      newRotation
    )
    // console.log(`Prev hyp: ${this.prevVelocityHypotenuseXZ} Cur hyp: ${velocityHypotenuseXZ}`)
    if (
      gameConfig.antiSlowEnabled && !isStart && !player.isStuck &&
    (
      (this.prevVelocityHypotenuseXZ - velocityHypotenuseXZ) > gameConfig.antiSlowThreshold ||
      (this.prevVelocityHypotenuseXZ - velocityHypotenuseXZ) < -gameConfig.antiBoostThreshold
    )
    ) {

      console.log(`Zabranuje sa extremnu vykyvu, zmena rychlosti bola: %c${
        this.prevVelocityHypotenuseXZ - velocityHypotenuseXZ
      }`, 'color: red')
      this.velocity.x = this.prevVelocity.x
      this.velocity.z = this.prevVelocity.z

    } else {

      this.prevVelocityHypotenuseXZ = velocityHypotenuseXZ
      this.velocity.set(
        newVelocityXZ.x,
        velocityChanged.y,
        newVelocityXZ.z * -1
      )

    }
    // console.log(`Velocity: x:${this.velocity.x} y:${this.velocity.y} z:${this.velocity.z}`)

    this.prevVelocity = this.velocity.clone()

    return this.velocity

  }

  /**
   * Ci je aktivny autotuck (ci uz forced alebo normalne)
   * @returns Ci je aktivny
   */
  public isAutoTuck(): boolean {

    return Number(import.meta.env.VITE_APP_AUTOTUCK_FORCED) === 1 ||
            settings.getSetting(SettingsTypes.controlsType) === ControlsTypes.autoTuck

  }

  /**
   * Auto tuck na mobiloch
   */
  private autoTuck(): void {

    if (!modes.isTutorial() && !this.isAutoTuck()) return

    const playerInputsUnitCyclePercentage = this.playerInputsUnitCycle.calculatePercentage()
    const isTuck = Math.abs(playerInputsUnitCyclePercentage) <= gameConfig.autoTuckLimit

    tuckState().isTuck = isTuck

  }

  /**
   * Nastavenie poslednej rotacie z aktualnej rychlosti na vypocty v dalsom cykle
   * @param velocityOriginal - rychlost
   * @returns Specialna rotacia
   */
  public setLastRotationFromVelocity(velocityOriginal: CANNON.Vec3): number {

    let velocity = velocityOriginal.clone()

    // na zaciatku musime dat manualne pozeranie sa dopredu, lebo ziadna velocity este nie je
    if (velocity.x === 0 && velocity.y === 0 && velocity.z === 0) {

      velocity = gameConfig.startVelocityDirection

    }

    this.lastPlayerRotationY = this.getRotationYFromVelocity(velocity)

    // UI update
    directionsState().player = THREE.MathUtils.radToDeg(this.lastPlayerRotationY)

    return this.lastPlayerRotationY

  }

  /**
   * Pohyb lyziara
   */
  private move(inAir = false): void {

    this.autoTuck()

    if (tuckState().isTuck || (!MobileDetector.isMobile() && inputsManager.actionPressed)) {

      player.isCrouching = true
      if (this.maxSpeed < gameConfig.maxSpeedCrouch) this.maxSpeed += 0.2

    } else {

      player.isCrouching = false
      if (this.maxSpeed > gameConfig.maxSpeedNormal) this.maxSpeed -= 0.1

    }

    // vypocitame novu rychlost
    player.physicsBody.velocity = this.getNewVelocity(player.physicsBody.velocity, inAir)

  }

  /**
   * Skontrolovanie min a max rychlosti lyziara
   */
  private checkMinMaxSpeed(): void {

    const { velocity } = player.physicsBody

    if (velocity.x > this.maxSpeed) player.physicsBody.velocity.x = this.maxSpeed
    if (velocity.x < -this.maxSpeed) player.physicsBody.velocity.x = -this.maxSpeed

    if (velocity.z > this.maxSpeed) player.physicsBody.velocity.z = this.maxSpeed
    if (velocity.z < -this.maxSpeed) player.physicsBody.velocity.z = -this.maxSpeed

  }

  /**
   * Aktualizovanie rychlosti kratko po starte
   */
  private updateStartingVelocity(): void {

    const { startSpeedUpCoef } = gameConfig

    const velocity = new CANNON.Vec3(
      player.physicsBody.velocity.x,
      player.physicsBody.velocity.y,
      player.physicsBody.velocity.z - startSpeedUpCoef
    )

    player.physicsBody.velocity = this.getNewVelocity(velocity)

  }

  /**
   * Aktualizovanie rychlosti
   */
  public update(inAir = false): void {

    // rychlost upravujeme iba vtedy, ked nie je hrac vo vzduchu a ma aktivne pohybove animacie
    if (player.activeUpdatingMovementAnimations && !player.jumpStates.inAir) {

      this.move(inAir)
      this.checkMinMaxSpeed()

    }

    // rychlost upravujeme specialne aj na zaciatku, kedy este nie su aktivne pohybove animacie
    if (player.isSkating) this.updateStartingVelocity()

    // UI update
    mainState().$patch({
      maxSpeed: this.maxSpeed,
      velocityX: player.physicsBody.velocity.x,
      velocityY: player.physicsBody.velocity.y,
      velocityZ: player.physicsBody.velocity.z
    })

  }

  /**
   * Resetovanie
   */
  public reset(): void {

    this.velocity = new CANNON.Vec3()
    this.playerInputsUnitCycle.actualValue = 0


  }

}
